import { ReactNode, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { styled, Typography, TypographyProps } from '@mui/material';

import { selectAccessibilityPrefs } from '../../db/app/selectors';
import { useAppSelector } from '../../store/hooks';

const usePageTitle = () => {
  const rootRef = useRef<HTMLHeadingElement>(null);

  const { turnOff } = useAppSelector((state) => selectAccessibilityPrefs(state));
  const isScreenReaderOn = !turnOff;

  const location = useLocation();

  useEffect(
    function onNavigation() {
      // wait for popovers like hamburger menu to close
      new Promise((resolve) => setTimeout(resolve, 0)).then(() => {
        if (!isScreenReaderOn) return;
        if (!rootRef.current) return;
        rootRef.current.focus();
      });
    },
    [location, isScreenReaderOn]
  );

  return { rootRef, isScreenReaderOn };
};

const Root = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[900],
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
})) as typeof Typography;

export interface PageTitleProps {
  /** @default 'h6Bold */
  variant?: TypographyProps['variant'];
  children: ReactNode;
}

const PageTitle = ({ variant = 'h6Bold', children }: PageTitleProps) => {
  const { rootRef, isScreenReaderOn } = usePageTitle();

  return (
    <Root
      ref={rootRef}
      variant={variant}
      component="h1"
      aria-current="page"
      tabIndex={isScreenReaderOn ? 0 : undefined}
    >
      {children}
    </Root>
  );
};

export default PageTitle;
